import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Card,
  Button,
  message,
  Space,
  Input,
  Modal,
  Spin,
  Tag,
  Popconfirm,
  Tooltip,
  List,
  Select,
  Tabs,
  Alert,
  Checkbox
} from 'antd';
import {
  ReloadOutlined,
  EditOutlined,
  SearchOutlined,
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  PaperClipOutlined,
  GoogleOutlined,
  FileOutlined,
  GlobalOutlined,
  TranslationOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  MailOutlined
} from '@ant-design/icons';
import { supabase } from '../../api/supabase';
import { colors, spacing, radius } from '../../styles/monochromeTheme';
import { isGoogleAuthenticated, getGoogleAuthUrl, getValidAccessToken } from '../../api/googleDrive';
import RichTextEditor from '../RichTextEditor';

const { Option } = Select;

function QuickReplies() {
  const [replies, setReplies] = useState([]);
  const [filteredReplies, setFilteredReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedReply, setSelectedReply] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [googleDriveFiles, setGoogleDriveFiles] = useState([]);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [driveSearchQuery, setDriveSearchQuery] = useState('');

  // État pour l'éditeur multilingue
  const [activeTab, setActiveTab] = useState('fr');
  const [frenchData, setFrenchData] = useState({
    id: null,
    title: '',
    subject: '',
    recipients: '',
    content: ''
  });
  const [englishData, setEnglishData] = useState({
    id: null,
    title: '',
    subject: '',
    recipients: '',
    content: ''
  });
  const [replyGroupId, setReplyGroupId] = useState(null);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [showEmailFields, setShowEmailFields] = useState(false);

  useEffect(() => {
    fetchReplies();

    const subscription = supabase
      .channel('quick_replies_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quick_replies'
        },
        () => {
          fetchReplies();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchReplies = async () => {
    setLoading(true);
    try {
      const { data: repliesData, error: repliesError } = await supabase
        .from('quick_replies')
        .select('*')
        .order('created_at', { ascending: false });

      if (repliesError) throw repliesError;

      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from('quick_reply_attachments')
        .select('*');

      const attachments = attachmentsError ? [] : (attachmentsData || []);

      const repliesWithAttachments = (repliesData || []).map(reply => ({
        ...reply,
        attachments: attachments.filter(att => att.reply_id === reply.id)
      }));

      // Grouper les réponses par reply_group_id
      const groupedReplies = groupRepliesByGroup(repliesWithAttachments);

      setReplies(groupedReplies);
      applyFilters(groupedReplies, searchText, languageFilter);

      // Synchroniser avec Chrome Storage pour l'extension
      syncWithChromeStorage(repliesWithAttachments);
    } catch (error) {
      message.error('Erreur lors du chargement: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Grouper les réponses par reply_group_id
  const groupRepliesByGroup = (repliesData) => {
    const grouped = {};
    const standalone = [];

    repliesData.forEach(reply => {
      if (reply.reply_group_id) {
        if (!grouped[reply.reply_group_id]) {
          grouped[reply.reply_group_id] = {
            reply_group_id: reply.reply_group_id,
            replies: {},
            attachments: reply.attachments || [],
            created_at: reply.created_at
          };
        }
        grouped[reply.reply_group_id].replies[reply.language] = reply;
        // Garder la date la plus ancienne
        if (new Date(reply.created_at) < new Date(grouped[reply.reply_group_id].created_at)) {
          grouped[reply.reply_group_id].created_at = reply.created_at;
        }
      } else {
        // Réponse sans groupe - créer un groupe virtuel
        standalone.push({
          reply_group_id: `standalone_${reply.id}`,
          replies: { [reply.language]: reply },
          attachments: reply.attachments || [],
          created_at: reply.created_at,
          isStandalone: true
        });
      }
    });

    // Convertir en tableau et trier par date
    const result = [...Object.values(grouped), ...standalone];
    result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return result;
  };

  const syncWithChromeStorage = (repliesData) => {
    try {
      const formattedReplies = repliesData.map(r => ({
        id: r.id.toString(),
        title: r.title,
        subject: r.subject || '',
        recipients: r.recipients || '',
        content: r.content,
        language: r.language || 'fr',
        reply_group_id: r.reply_group_id,
        attachments: r.attachments || []
      }));

      // Pour Chrome Extension
      // eslint-disable-next-line no-undef
      if (typeof chrome !== 'undefined' && chrome?.storage) {
        // eslint-disable-next-line no-undef
        chrome.storage.sync.set({ quickReplies: formattedReplies });
      }

      // Backup en localStorage
      localStorage.setItem('quickReplies', JSON.stringify(formattedReplies));
    } catch (err) {
      console.error('Erreur sync Chrome Storage:', err);
    }
  };

  const applyFilters = (data, search, lang) => {
    let filtered = data;

    if (lang !== 'all') {
      // Filtrer les groupes qui ont la langue demandée
      filtered = filtered.filter(group => group.replies[lang]);
    }

    if (search) {
      const query = search.toLowerCase();
      filtered = filtered.filter(group => {
        const frReply = group.replies.fr;
        const enReply = group.replies.en;
        return (
          frReply?.title?.toLowerCase().includes(query) ||
          enReply?.title?.toLowerCase().includes(query) ||
          frReply?.subject?.toLowerCase().includes(query) ||
          enReply?.subject?.toLowerCase().includes(query) ||
          stripHtml(frReply?.content)?.toLowerCase().includes(query) ||
          stripHtml(enReply?.content)?.toLowerCase().includes(query)
        );
      });
    }

    setFilteredReplies(filtered);
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleSearch = (value) => {
    setSearchText(value);
    applyFilters(replies, value, languageFilter);
  };

  const handleLanguageFilter = (value) => {
    setLanguageFilter(value);
    applyFilters(replies, searchText, value);
  };

  const handleAdd = () => {
    setSelectedReply(null);
    setReplyGroupId(null);
    setFrenchData({ id: null, title: '', subject: '', recipients: '', content: '' });
    setEnglishData({ id: null, title: '', subject: '', recipients: '', content: '' });
    setCurrentAttachments([]);
    setGoogleDriveFiles([]);
    setDriveSearchQuery('');
    setShowEmailFields(false);
    setActiveTab('fr');
    setEditModalVisible(true);
  };

  const handleEdit = (group) => {
    setSelectedReply(group);
    setReplyGroupId(group.reply_group_id);

    const frReply = group.replies.fr;
    const enReply = group.replies.en;

    setFrenchData({
      id: frReply?.id || null,
      title: frReply?.title || '',
      subject: frReply?.subject || '',
      recipients: frReply?.recipients || '',
      content: frReply?.content || ''
    });

    setEnglishData({
      id: enReply?.id || null,
      title: enReply?.title || '',
      subject: enReply?.subject || '',
      recipients: enReply?.recipients || '',
      content: enReply?.content || ''
    });

    // Charger les pièces jointes existantes
    const firstReply = frReply || enReply;
    setCurrentAttachments(group.attachments || firstReply?.attachments || []);
    setGoogleDriveFiles([]);
    setDriveSearchQuery('');

    // Afficher les champs email si un subject ou recipients existe déjà
    setShowEmailFields(!!(frReply?.subject || enReply?.subject || frReply?.recipients || enReply?.recipients));

    // Ouvrir sur l'onglet qui a du contenu, ou FR par défaut
    setActiveTab(frReply ? 'fr' : 'en');
    setEditModalVisible(true);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      // Générer un reply_group_id si nouveau
      let groupId = replyGroupId;
      if (!groupId || groupId.startsWith('standalone_')) {
        groupId = crypto.randomUUID();
        setReplyGroupId(groupId);
      }

      // Sauvegarder version française si remplie
      if (frenchData.title || frenchData.content) {
        if (frenchData.id) {
          const { error } = await supabase
            .from('quick_replies')
            .update({
              title: frenchData.title,
              subject: frenchData.subject || null,
              recipients: frenchData.recipients || null,
              content: frenchData.content,
              reply_group_id: groupId,
              updated_at: new Date().toISOString()
            })
            .eq('id', frenchData.id);

          if (error) throw error;
        } else {
          const { data, error } = await supabase
            .from('quick_replies')
            .insert([{
              title: frenchData.title,
              subject: frenchData.subject || null,
              recipients: frenchData.recipients || null,
              content: frenchData.content,
              language: 'fr',
              reply_group_id: groupId,
              created_at: new Date().toISOString()
            }])
            .select()
            .single();

          if (error) throw error;
          setFrenchData({ ...frenchData, id: data.id });
        }
      }

      // Sauvegarder version anglaise si remplie
      if (englishData.title || englishData.content) {
        if (englishData.id) {
          const { error } = await supabase
            .from('quick_replies')
            .update({
              title: englishData.title,
              subject: englishData.subject || null,
              recipients: englishData.recipients || null,
              content: englishData.content,
              reply_group_id: groupId,
              updated_at: new Date().toISOString()
            })
            .eq('id', englishData.id);

          if (error) throw error;
        } else {
          const { data, error } = await supabase
            .from('quick_replies')
            .insert([{
              title: englishData.title,
              subject: englishData.subject || null,
              recipients: englishData.recipients || null,
              content: englishData.content,
              language: 'en',
              reply_group_id: groupId,
              created_at: new Date().toISOString()
            }])
            .select()
            .single();

          if (error) throw error;
          setEnglishData({ ...englishData, id: data.id });
        }
      }

      // Sauvegarder les nouvelles pièces jointes
      const newAttachments = currentAttachments.filter(att => att.isNew);
      if (newAttachments.length > 0) {
        // Utiliser l'ID français en priorité, sinon anglais
        const replyIdForAttachments = frenchData.id || englishData.id;

        if (replyIdForAttachments) {
          for (const att of newAttachments) {
            const { error: attError } = await supabase
              .from('quick_reply_attachments')
              .insert([{
                reply_id: replyIdForAttachments,
                file_name: att.file_name,
                google_drive_file_id: att.google_drive_file_id,
                file_type: att.file_type,
                file_size: att.file_size,
                drive_url: att.drive_url,
                created_at: new Date().toISOString()
              }]);

            if (attError) {
              console.error('Erreur ajout pièce jointe:', attError);
            }
          }
        }
      }

      message.success('Réponse(s) sauvegardée(s) avec succès!');
      setEditModalVisible(false);
      fetchReplies();
    } catch (error) {
      message.error('Erreur: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    try {
      // Supprimer toutes les versions du groupe
      const idsToDelete = Object.values(group.replies).map(r => r.id);

      for (const id of idsToDelete) {
        const { error } = await supabase
          .from('quick_replies')
          .delete()
          .eq('id', id);

        if (error) throw error;
      }

      message.success('Réponse(s) supprimée(s) avec succès!');
      fetchReplies();
    } catch (error) {
      message.error('Erreur lors de la suppression: ' + error.message);
    }
  };

  const handleCopy = async (content, id) => {
    try {
      const textContent = stripHtml(content);
      await navigator.clipboard.writeText(textContent);
      setCopiedId(id);
      message.success('Copié dans le presse-papier!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      message.error('Erreur lors de la copie');
    }
  };

  const loadGoogleDriveFiles = async (searchQuery = '') => {
    setLoadingDrive(true);
    try {
      if (!isGoogleAuthenticated()) {
        message.warning('Veuillez vous connecter à Google Drive');
        window.location.href = getGoogleAuthUrl();
        return;
      }

      const accessToken = await getValidAccessToken();

      // Construire la requête de recherche
      let queryParts = ["mimeType != 'application/vnd.google-apps.folder'", "trashed = false"];

      if (searchQuery && searchQuery.trim()) {
        // Rechercher par nom de fichier
        queryParts.push(`name contains '${searchQuery.trim().replace(/'/g, "\\'")}'`);
      }

      const fullQuery = queryParts.join(' and ');

      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?` +
        `q=${encodeURIComponent(fullQuery)}&` +
        `fields=files(id,name,mimeType,webViewLink,size,createdTime)&` +
        `orderBy=modifiedTime desc&` +
        `pageSize=100`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Erreur lors du chargement des fichiers');
      }

      const data = await response.json();
      setGoogleDriveFiles(data.files || []);

      if (searchQuery && data.files?.length === 0) {
        message.info('Aucun fichier trouvé pour cette recherche');
      }
    } catch (error) {
      message.error('Erreur: ' + error.message);
    } finally {
      setLoadingDrive(false);
    }
  };

  const handleDriveSearch = () => {
    loadGoogleDriveFiles(driveSearchQuery);
  };

  const addAttachmentFromDrive = (file) => {
    // Vérifier si le fichier est déjà dans la liste
    const alreadyExists = currentAttachments.some(att => att.google_drive_file_id === file.id);
    if (alreadyExists) {
      message.warning('Ce fichier est déjà ajouté');
      return;
    }

    // Ajouter au state local (sera sauvegardé avec handleSave)
    const newAttachment = {
      id: `temp_${Date.now()}`, // ID temporaire
      file_name: file.name,
      google_drive_file_id: file.id,
      file_type: file.mimeType,
      file_size: file.size,
      drive_url: file.webViewLink,
      isNew: true // Marqueur pour savoir que c'est nouveau
    };

    setCurrentAttachments([...currentAttachments, newAttachment]);
    message.success(`${file.name} ajouté!`);
  };

  const removeAttachmentFromState = (attachmentId) => {
    setCurrentAttachments(currentAttachments.filter(att => att.id !== attachmentId));
    message.success('Pièce jointe retirée!');
  };

  const removeAttachmentFromDB = async (attachmentId) => {
    try {
      const { error } = await supabase
        .from('quick_reply_attachments')
        .delete()
        .eq('id', attachmentId);

      if (error) throw error;

      // Retirer aussi du state local
      setCurrentAttachments(currentAttachments.filter(att => att.id !== attachmentId));
      message.success('Pièce jointe supprimée!');
    } catch (error) {
      message.error('Erreur: ' + error.message);
    }
  };

  // Memoize stats
  const stats = useMemo(() => {
    let frCount = 0;
    let enCount = 0;
    let bothCount = 0;

    replies.forEach(group => {
      if (group.replies.fr && group.replies.en) {
        bothCount++;
      } else if (group.replies.fr) {
        frCount++;
      } else if (group.replies.en) {
        enCount++;
      }
    });

    return { frCount, enCount, bothCount, total: replies.length };
  }, [replies]);

  const columns = [
    {
      title: 'Titre',
      key: 'title',
      width: 250,
      fixed: 'left',
      sorter: (a, b) => {
        const titleA = a.replies.fr?.title || a.replies.en?.title || '';
        const titleB = b.replies.fr?.title || b.replies.en?.title || '';
        return titleA.localeCompare(titleB);
      },
      render: (_, group) => {
        const frReply = group.replies.fr;
        const enReply = group.replies.en;
        const mainTitle = frReply?.title || enReply?.title;

        return (
          <Space direction="vertical" size="small">
            <strong>{mainTitle}</strong>
            <Space wrap size={4}>
              {frReply && (
                <Tag color="blue">🇫🇷 FR</Tag>
              )}
              {enReply && (
                <Tag color="green">🇬🇧 EN</Tag>
              )}
              {frReply && enReply && (
                <Tag color="purple" icon={<TranslationOutlined />}>
                  Bilingue
                </Tag>
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Contenu (aperçu)',
      key: 'content',
      ellipsis: { showTitle: false },
      render: (_, group) => {
        const frContent = stripHtml(group.replies.fr?.content);
        const enContent = stripHtml(group.replies.en?.content);

        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {frContent && (
              <Tooltip title={frContent}>
                <div style={{
                  maxWidth: 250,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '12px'
                }}>
                  🇫🇷 {frContent}
                </div>
              </Tooltip>
            )}
            {enContent && (
              <Tooltip title={enContent}>
                <div style={{
                  maxWidth: 250,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '12px'
                }}>
                  🇬🇧 {enContent}
                </div>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, group) => {
        const frReply = group.replies.fr;
        const enReply = group.replies.en;
        const mainReply = frReply || enReply;

        return (
          <Space size="small">
            <Tooltip title="Copier FR">
              {frReply && (
                <Button
                  icon={copiedId === frReply.id ? <CheckCircleOutlined /> : <CopyOutlined />}
                  onClick={() => handleCopy(frReply.content, frReply.id)}
                  size="small"
                  type={copiedId === frReply.id ? "primary" : "default"}
                >
                  🇫🇷
                </Button>
              )}
            </Tooltip>
            <Tooltip title="Copier EN">
              {enReply && (
                <Button
                  icon={copiedId === enReply.id ? <CheckCircleOutlined /> : <CopyOutlined />}
                  onClick={() => handleCopy(enReply.content, enReply.id)}
                  size="small"
                  type={copiedId === enReply.id ? "primary" : "default"}
                >
                  🇬🇧
                </Button>
              )}
            </Tooltip>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(group)}
              size="small"
              type="primary"
              style={{ background: colors.bgDark }}
            >
              Éditer
            </Button>
            <Popconfirm
              title="Supprimer cette réponse?"
              description="Cela supprimera les versions FR et EN."
              onConfirm={() => handleDelete(group)}
              okText="Oui"
              cancelText="Non"
              okButtonProps={{ danger: true }}
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const tabItems = [
    {
      key: 'fr',
      label: (
        <span>
          🇫🇷 Français
          {frenchData.title && <Tag color="blue" style={{ marginLeft: 8 }}>✓</Tag>}
        </span>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Titre (FR) *
            </label>
            <Input
              value={frenchData.title}
              onChange={(e) => setFrenchData({ ...frenchData, title: e.target.value })}
              placeholder="Ex: Devis yacht charter"
              size="large"
            />
          </div>

          {showEmailFields && (
            <>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  <Space>
                    <MailOutlined style={{ color: '#722ed1' }} />
                    Destinataires (FR)
                  </Space>
                </label>
                <Input
                  value={frenchData.recipients}
                  onChange={(e) => setFrenchData({ ...frenchData, recipients: e.target.value })}
                  placeholder="Ex: client@email.com, autre@email.com"
                  size="large"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  <Space>
                    <MailOutlined style={{ color: '#722ed1' }} />
                    Objet de l'email (FR)
                  </Space>
                </label>
                <Input
                  value={frenchData.subject}
                  onChange={(e) => setFrenchData({ ...frenchData, subject: e.target.value })}
                  placeholder="Ex: Devis yacht - [DATE+1] - Master Ski Pilou"
                  size="large"
                />
              </div>
            </>
          )}

          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Contenu du message (FR)
            </label>
            <RichTextEditor
              value={frenchData.content}
              onChange={(html) => setFrenchData({ ...frenchData, content: html })}
              placeholder="Bonjour [NOM], Suite à votre demande..."
            />
          </div>
        </Space>
      ),
    },
    {
      key: 'en',
      label: (
        <span>
          🇬🇧 English
          {englishData.title && <Tag color="green" style={{ marginLeft: 8 }}>✓</Tag>}
        </span>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Title (EN) *
            </label>
            <Input
              value={englishData.title}
              onChange={(e) => setEnglishData({ ...englishData, title: e.target.value })}
              placeholder="Ex: Yacht charter quote"
              size="large"
            />
          </div>

          {showEmailFields && (
            <>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  <Space>
                    <MailOutlined style={{ color: '#722ed1' }} />
                    Recipients (EN)
                  </Space>
                </label>
                <Input
                  value={englishData.recipients}
                  onChange={(e) => setEnglishData({ ...englishData, recipients: e.target.value })}
                  placeholder="Ex: client@email.com, other@email.com"
                  size="large"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  <Space>
                    <MailOutlined style={{ color: '#722ed1' }} />
                    Email Subject (EN)
                  </Space>
                </label>
                <Input
                  value={englishData.subject}
                  onChange={(e) => setEnglishData({ ...englishData, subject: e.target.value })}
                  placeholder="Ex: Yacht quote - [DATE+1] - Master Ski Pilou"
                  size="large"
                />
              </div>
            </>
          )}

          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Message Content (EN)
            </label>
            <RichTextEditor
              value={englishData.content}
              onChange={(html) => setEnglishData({ ...englishData, content: html })}
              placeholder="Dear [NAME], Following your request..."
            />
          </div>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: colors.bgPrimary, padding: spacing.xxl }}>
      <Card style={{ borderRadius: radius.xxxl, border: `1px solid ${colors.borderLight}` }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                Réponses Rapides Email
              </h1>
              <p style={{ margin: '8px 0 0 0', color: colors.textSecondary }}>
                Templates bilingues FR/EN avec éditeur riche
              </p>
            </div>
            <Space>
              <Select
                value={languageFilter}
                onChange={handleLanguageFilter}
                style={{ width: 150 }}
              >
                <Option value="all">Toutes</Option>
                <Option value="fr">🇫🇷 Français</Option>
                <Option value="en">🇬🇧 English</Option>
              </Select>
              <Input
                placeholder="Rechercher..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchReplies}
                loading={loading}
              >
                Actualiser
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                style={{ background: colors.bgDark }}
              >
                Nouvelle Réponse
              </Button>
            </Space>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Space>
                <Tag color="blue">
                  {filteredReplies.length} réponse{filteredReplies.length > 1 ? 's' : ''}
                </Tag>
                <Tag color="purple" icon={<TranslationOutlined />}>
                  {stats.bothCount} bilingue{stats.bothCount > 1 ? 's' : ''}
                </Tag>
                <Tag color="green" icon={<GlobalOutlined />}>
                  🇫🇷 {stats.frCount + stats.bothCount} · 🇬🇧 {stats.enCount + stats.bothCount}
                </Tag>
              </Space>
              <Table
                columns={columns}
                dataSource={filteredReplies}
                rowKey="reply_group_id"
                bordered
                pagination={{
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total) => `Total: ${total} réponses`
                }}
                scroll={{ x: 1200 }}
              />
            </>
          )}
        </Space>
      </Card>

      {/* Modal d'édition multilingue */}
      <Modal
        title={
          <Space>
            <TranslationOutlined />
            {selectedReply ? 'Modifier la réponse' : 'Nouvelle Réponse Rapide'}
          </Space>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Alert
          message="Variables disponibles"
          description={
            <div>
              <strong>Auto-remplacées:</strong> [SIGNATURE] [DATE] [DATE+1] [DATE+7] [DATE+30] [HEURE] [ANNEE] [MOIS]
              <br />
              <strong>À remplir:</strong> [NOM] [YACHT] [MONTANT] [DURÉE] [LIEU]
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: '16px' }}
        />

        <div style={{ marginBottom: '16px' }}>
          <Checkbox
            checked={showEmailFields}
            onChange={(e) => setShowEmailFields(e.target.checked)}
          >
            <Space>
              <MailOutlined style={{ color: '#722ed1' }} />
              Ajouter destinataires et objet d'email
            </Space>
          </Checkbox>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />

        {/* Section Pièces jointes */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#fafafa',
          borderRadius: '8px',
          border: '1px solid #f0f0f0'
        }}>
          <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PaperClipOutlined />
            Pièces jointes ({currentAttachments.length})
          </h4>

          {/* Liste des pièces jointes actuelles */}
          {currentAttachments.length > 0 && (
            <List
              dataSource={currentAttachments}
              size="small"
              style={{ marginBottom: '16px', background: 'white', borderRadius: '6px' }}
              renderItem={(att) => (
                <List.Item
                  style={{ padding: '8px 12px' }}
                  actions={[
                    <Button
                      key="view"
                      type="link"
                      href={att.drive_url}
                      target="_blank"
                      icon={<GoogleOutlined />}
                      size="small"
                    >
                      Voir
                    </Button>,
                    <Popconfirm
                      key="delete"
                      title="Retirer cette pièce jointe?"
                      onConfirm={() => {
                        if (att.isNew) {
                          removeAttachmentFromState(att.id);
                        } else {
                          removeAttachmentFromDB(att.id);
                        }
                      }}
                      okText="Oui"
                      cancelText="Non"
                    >
                      <Button type="link" danger icon={<DeleteOutlined />} size="small">
                        Retirer
                      </Button>
                    </Popconfirm>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<FileOutlined style={{ fontSize: '18px', color: '#1890ff' }} />}
                    title={
                      <span>
                        {att.file_name}
                        {att.isNew && <Tag color="green" style={{ marginLeft: 8 }}>Nouveau</Tag>}
                      </span>
                    }
                    description={<span style={{ fontSize: '11px' }}>{att.file_type?.split('/').pop() || 'fichier'}</span>}
                  />
                </List.Item>
              )}
            />
          )}

          {/* Recherche Google Drive */}
          <div>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
              Ajouter depuis Google Drive
            </label>
            <Input.Search
              placeholder="Rechercher un fichier dans Google Drive..."
              value={driveSearchQuery}
              onChange={(e) => setDriveSearchQuery(e.target.value)}
              onSearch={handleDriveSearch}
              enterButton={
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  loading={loadingDrive}
                  style={{ background: colors.bgDark }}
                >
                  Rechercher
                </Button>
              }
              allowClear
              onClear={() => {
                setDriveSearchQuery('');
                setGoogleDriveFiles([]);
              }}
            />
          </div>

          {/* Résultats de recherche Google Drive */}
          {googleDriveFiles.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ marginBottom: '8px', color: '#666', fontSize: '12px' }}>
                {googleDriveFiles.length} fichier{googleDriveFiles.length > 1 ? 's' : ''} trouvé{googleDriveFiles.length > 1 ? 's' : ''}
              </div>
              <List
                dataSource={googleDriveFiles}
                size="small"
                style={{ maxHeight: '200px', overflow: 'auto', background: 'white', borderRadius: '6px', border: '1px solid #f0f0f0' }}
                renderItem={(file) => (
                  <List.Item
                    style={{ padding: '6px 12px' }}
                    actions={[
                      <Button
                        key="add"
                        type="primary"
                        size="small"
                        onClick={() => addAttachmentFromDrive(file)}
                        style={{ background: colors.bgDark }}
                      >
                        Ajouter
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileOutlined style={{ fontSize: '16px', color: '#1890ff' }} />}
                      title={<span style={{ fontSize: '13px' }}>{file.name}</span>}
                      description={<span style={{ fontSize: '10px', color: '#999' }}>{file.mimeType?.split('/').pop()}</span>}
                    />
                  </List.Item>
                )}
              />
            </div>
          )}
        </div>

        <div style={{
          textAlign: 'right',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <Space>
            <div style={{
              marginRight: '16px',
              color: '#666',
              fontSize: '12px'
            }}>
              {frenchData.title && '🇫🇷 '}
              {englishData.title && '🇬🇧 '}
              {!frenchData.title && !englishData.title && 'Remplissez au moins une version'}
            </div>
            <Button onClick={() => setEditModalVisible(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!frenchData.title && !englishData.title}
              style={{ background: colors.bgDark }}
            >
              Sauvegarder
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
}

export default QuickReplies;
